import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { staffList, createStaff, deleteStaff, editStaff, getUsersList } from "../services/user.service";
import { Formik, Form, Field, ErrorMessage } from "formik";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import * as Yup from "yup";
import { SWAL_SETTINGS } from "../../../utils/Constants";
import Swal from "sweetalert2";
import { handleServerValidations } from "../../../utils/commonfunction";
// --- Small reusable UI pieces ---
const Spinner = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-16 h-16 border-4 border-[#FF63B5] border-t-transparent rounded-full animate-spin"></div>
  </div>
);

// --- UserCard component ---
const UserCard = ({ user, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="relative bg-white p-[15px] md:p-[20px] rounded-[10px] shadow-[0px_2px_24px_0px_rgba(0,0,0,0.05)] border !border-zinc-100 flex items-start justify-between">
      <div className="flex items-center gap-[15px] md:gap-[20px]">
        <div className="md:h-[75px] md:w-[75px] h-[50px] w-[50px] rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
          <img 
            src={user.profile_image || "/images/User.png"} 
            onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
            alt={user.name} 
            className="h-full w-full object-cover" 
          />
        </div>

        <div>
          <h3 className="headings-h5 mb-2 text-zinc-950 leading-none">{user.name}</h3>
          <p className="body-md text-zinc-500 leading-snug">{user.email}</p>
          <p className="body-md text-zinc-500 leading-snug">{user.phone_number}</p>
        </div>
      </div>

      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen((s) => !s);
          }}
          className="text-gray-400 hover:text-gray-700 p-1 rounded-full transition duration-150 focus:outline-none"
          aria-label="More options">
          <img src="/images/dot.svg" alt="dot" />
        </button>

        {isMenuOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white border border-gray-100 rounded-lg shadow-lg z-10">
            <ul className="text-sm text-gray-700 m-0 p-0">
              <li
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                onClick={() => {
                  setIsMenuOpen(false);
                  onEdit(user);
                }}>
                {t('advertiser.my_team.edit_user')}
              </li>
              <li
                className="px-4 py-2 text-red-600 hover:bg-red-50 cursor-pointer"
                onClick={() => {
                  setIsMenuOpen(false);
                  onDelete(user);
                }}>
                {t('advertiser.my_team.delete')}
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

// --- Validation schema for Formik ---
const getStaffSchema = (t) => Yup.object().shape({
  name: Yup.string().when("selectedExistingUser", {
    is: (val) => !val,
    then: (schema) => schema.required(t("vaildation.name")),
    otherwise: (schema) => schema.notRequired(),
  }),
  email: Yup.string().email(t("vaildation.email_invalid")).required(t("vaildation.email_required")),
});

// --- Main MyTeam component ---
export default function MyTeam() {
  const { t } = useTranslation();
  const DEFAULT_PERMISSIONS = [
    { name: t('advertiser.my_team.permissions_list.accept_product'), value: "accept_product", selected: false },
    { name: t('advertiser.my_team.permissions_list.create_product'), value: "create_product", selected: false },
    { name: t('advertiser.my_team.permissions_list.edit_product'), value: "edit_product", selected: false },
    { name: t('advertiser.my_team.permissions_list.delete_product'), value: "delete_product", selected: false },
    { name: t('advertiser.my_team.permissions_list.reply_on_comments'), value: "reply_on_comments", selected: false },
    { name: t('advertiser.my_team.permissions_list.stream_allocation'), value: "stream_allocation", selected: false },
    { name: t('advertiser.my_team.permissions_list.view_all_product'), value: "view_all_product", selected: false },
  ];
  const staffSchema = getStaffSchema(t);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedToDelete, setSelectedToDelete] = useState(null);

  const [editingUser, setEditingUser] = useState(null);

  const [query, setQuery] = useState("");
  const debounceRef = useRef(null);

  // Fetch staff list
  const fetchStaff = async (search = "") => {
    setLoading(true);
    setError(null);
    try {
      const resp = await staffList({ global_search: search });

      if (resp?.success && resp?.data?.list) {
        setStaff(resp.data.list);
      } else {
        setStaff([]);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || t('advertiser.my_team.fetch_failed'));
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchStaff();
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchStaff(query);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // Open create modal (for new or edit)
  const openCreateModal = (user = null) => {
    setEditingUser(user);
    setIsCreateOpen(true);
  };

  const closeCreateModal = () => {
    setEditingUser(null);
    setIsCreateOpen(false);
  };

  // Delete flow
  const confirmDelete = (user) => {
    setSelectedToDelete(user);
    setIsConfirmOpen(true);
  };

  const cancelDelete = () => {
    setSelectedToDelete(null);
    setIsConfirmOpen(false);
  };

  const handleDelete = async () => {
    if (!selectedToDelete) return;
    try {
      setLoading(true);
      await deleteStaff({ _id: selectedToDelete._id })
        .then((response) => {
          if (response.success) {
            Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          } else {
            Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
          }
        })
        .catch((err) => {
          console.log(err);
        });
      // refetch
      await fetchStaff(query);
      setIsConfirmOpen(false);
      setSelectedToDelete(null);
    } catch (err) {
      console.error(err);
      setError(err?.message || t('advertiser.my_team.delete_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Submit handler for create/edit

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("email", values.email);
      // formData.append("phone_number", values.phone_number);

      const permissionValues = (values.permissions || []).filter((p) => p.selected).map((p) => p.value);

      formData.append("permissions", JSON.stringify(permissionValues));

      if (values.profile_image) {
        formData.append("profile_image", values.profile_image);
      }

      // If editing, include id
      if (values._id) formData.append("_id", values._id);

      // ⭐ CHANGED HERE — choose correct API:
      const apiCall = values._id ? editStaff : createStaff;

      await apiCall(formData)
        .then((response) => {
          if (response.success) {
            Swal.fire({ icon: "success", text: response.message, ...SWAL_SETTINGS });
          } else {
            Swal.fire({
              icon: "error",
              text: handleServerValidations(response),
              ...SWAL_SETTINGS,
            });
          }
        })
        .catch((err) => {
          console.log(err);
        });

      await fetchStaff(query);

      resetForm();
      closeCreateModal();
    } catch (err) {
      console.error(err);
      setError(err?.message || t('advertiser.my_team.save_failed'));
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <>
      <div className="dashbaord-bg py-10 h-[calc(100vh-200px)] overflow-y-auto">
        <div className="container">
          <div className="border-b border-gray-200 pb-[15px] mb-[30px] flex items-center justify-between flex-wrap gap-[15px]">
            <h1 className="headings-h1 text-zinc-950">{t('advertiser.my_team.heading')}</h1>
            <div className="flex gap-3 items-center">
              <div className="search-box relative">
                <input value={query} onChange={(e) => setQuery(e.target.value)} type="text" className="form-control-custom !pr-[50px] min-w-[250px] md:min-w-[350px]" placeholder={t('advertiser.my_team.search_placeholder')} />
                <button className="absolute right-2 top-2">
                  <img src="/images/search1.svg" alt="search" />
                </button>
              </div>

              <button className="btn btn-pri !bg-[#FF63B5] !border-[#FF63B5] min-w-[175px]" onClick={() => openCreateModal(null)}>
                {t('advertiser.my_team.invite_user')}
              </button>
            </div>
          </div>

          {/* {loading && <Spinner />} */}
          {error && <div className="text-red-600 mb-4">{error}</div>}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {staff.length === 0 && !loading ? (
              <div className="col-span-full text-center text-gray-500">
                {t('advertiser.my_team.no_staff')}
              </div>
            ) : (
              staff.map((user) => (
                <UserCard
                  key={user._id ?? user.id}
                  user={user}
                  onEdit={openCreateModal}
                  onDelete={confirmDelete}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {isCreateOpen && (
        <dialog open className="modal p-[15px]" aria-modal>
          <div className="modal-box max-w-[645px] !w-full">
            <h2 className="headings-h1 text-left text-zinc-800 mb-4">{editingUser ? t('advertiser.my_team.edit_user') : t('advertiser.my_team.create_user')}</h2>

            <Formik
              initialValues={{
                _id: editingUser?._id ?? null,
                name: editingUser?.name ?? "",
                email: editingUser?.email ?? "",
                // phone_number: editingUser?.phone_number ?? "",
                permissions: editingUser
                  ? DEFAULT_PERMISSIONS.map((perm) => ({
                    ...perm,
                    selected: editingUser.permissions.includes(perm.value),
                  }))
                  : DEFAULT_PERMISSIONS.map((p) => ({ ...p })),

                profile_image: null,
                country_code: editingUser?.country_code ?? "91",
                avatarPreview: editingUser?.profile_image
                  ? editingUser.profile_image // existing saved image
                  : null,
              }}
              validationSchema={staffSchema}
              onSubmit={handleSubmit}>
              {({ values, setFieldValue, isSubmitting }) => (
                <Form>
                  <div className="grid grid-cols-2 gap-[30px]">
                    <div className="form-group relative">
                      <label>{t('advertiser.my_team.email_address')}</label>
                      <Field
                        name="email"
                        type="email"
                        className="form-control-custom"
                        placeholder={t('advertiser.my_team.enter_email')}
                        autoComplete="off"
                        readOnly={values._id}   // ⭐ ADD THIS
                        onChange={async (e) => {
                          if (values._id) return; // prevent editing when editing user

                          const val = e.target.value;
                          setFieldValue("email", val);
                          setFieldValue("selectedExistingUser", null);

                          if (!values._id && val.length > 2) {
                            try {
                              const res = await getUsersList({ search: val });
                              if (res.success && res.data.length > 0) {
                                setFieldValue("userSuggestions", res.data);
                              } else {
                                setFieldValue("userSuggestions", []);
                              }
                            } catch (err) { }
                          } else {
                            setFieldValue("userSuggestions", []);
                          }
                        }}
                      />
                      {values.userSuggestions?.length > 0 && !values.selectedExistingUser && !values._id && (
                        <div className="absolute top-full left-0 z-[100] w-full bg-white border border-gray-100 mt-1 rounded-xl shadow-[0px_4px_20px_rgba(0,0,0,0.08)] max-h-60 overflow-y-auto p-2 flex flex-col gap-1">
                          {values.userSuggestions.map(u => {
                            return (
                              <div
                                key={u._id}
                                className={`p-4 rounded-xl cursor-pointer flex items-center justify-between transition-all duration-200 hover:bg-gray-50 border border-transparent`}
                                onClick={() => {
                                  setFieldValue("email", u.email);
                                  setFieldValue("name", u.name);
                                  if (u.phone_number) setFieldValue("phone_number", u.phone_number);
                                  setFieldValue("selectedExistingUser", u);
                                  setFieldValue("userSuggestions", []);
                                }}
                              >
                                <div className="flex items-center gap-4 min-w-0 flex-1">
                                  <div className="h-12 w-12 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 border border-black/5">
                                    <img 
                                      src={u.profile_image || "/images/User.png"} 
                                      onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
                                      alt={u.name} 
                                      className="h-full w-full object-cover" 
                                    />
                                  </div>
                                  <div className="flex flex-col min-w-0 flex-1">
                                    <span className="text-base font-bold text-gray-900 leading-tight truncate block max-w-full">{u.name}</span>
                                    <span className="text-sm text-gray-500 mt-0.5 truncate block max-w-full">{u.email}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div className="text-sm text-red-600 mt-1">
                        <ErrorMessage name="email" />
                      </div>
                    </div>

                    {!values.selectedExistingUser && (
                      <div className="form-group">
                        <label>{t('advertiser.my_team.name')}</label>
                        <Field name="name" type="text" readOnly={values._id} className="form-control-custom" placeholder={t('advertiser.my_team.enter_name')} />
                        <div className="text-sm text-red-600 mt-1">
                          <ErrorMessage name="name" />
                        </div>
                      </div>
                    )}
                  </div>

                  {values.selectedExistingUser && (
                    <div className="mb-6 p-4 rounded-xl border border-gray-100 shadow-[0px_2px_12px_rgba(0,0,0,0.04)] bg-white flex items-center justify-between w-full">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="h-14 w-14 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 border border-black/5">
                          <img 
                            src={values.selectedExistingUser.profile_image || "/images/User.png"} 
                            onError={(e) => { e.target.onerror = null; e.target.src = '/images/User.png'; }}
                            alt={values.selectedExistingUser.name} 
                            className="h-full w-full object-cover" 
                          />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="text-[17px] font-bold text-gray-900 leading-tight truncate block max-w-full">{values.selectedExistingUser.name}</span>
                          <span className="text-[15px] text-gray-500 mt-1 truncate block max-w-full">{values.selectedExistingUser.email}</span>
                        </div>
                      </div>
                      <div
                        className="h-6 w-6 rounded flex items-center justify-center bg-[#FF7E79] text-white shrink-0 cursor-pointer hover:bg-red-500 transition-colors ml-4"
                        onClick={() => {
                          setFieldValue("selectedExistingUser", null);
                          setFieldValue("email", "");
                          setFieldValue("name", "");
                          // setFieldValue("phone_number", "");
                        }}
                        title={t('advertiser.my_team.deselect_user')}
                      >
                        <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 5L4.5 8.5L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* {!values.selectedExistingUser && (
                    <>
                      <div className="form-group">
                        <label>Phone Number</label>

                        <div className="flex gap-2 items-center">

                     
                          <PhoneInput
                            country="in"
                            readOnly={values._id}
                            value={values.country_code}
                            onChange={(value, country) => {
                              setFieldValue("country_code", value);
                            }}
                            enableSearch
                            countryCodeEditable={false}
                            showSelectedDialCode={true}   
                            inputProps={{
                              readOnly: true
                            }}
                            containerStyle={{
                              width: "110px"
                            }}

                            inputStyle={{
                              width: "110px",
                              height: "43px",
                              borderRadius: "6px 0 0 6px",
                              border: "1px solid #ccc",
                              paddingLeft: "57px",  // space for flag
                            }}
                            buttonStyle={{
                              borderRadius: "5px",
                              border: "1px solid #ccc",
                            }}
                          />



                      
                          <input
                            type="text"
                            value={values.phone_number}
                            readOnly={values._id}
                            onChange={(e) =>
                              setFieldValue("phone_number", e.target.value.replace(/\D/g, ""))
                            }
                            className="form-control-custom flex-1"
                            placeholder={t('advertiser.my_team.placeholder_phone')}
                            maxLength={15}
                          />
                        </div>

                        <ErrorMessage
                          name="phone_number"
                          component="p"
                          className="text-red-500 text-sm mt-1"
                        />
                      </div>
                    </>
                  )} */}

                  <div className="permisson-box mb-[20px]">
                    <h3 className="text-[15px] text-gray-950">{t('advertiser.my_team.permissions')}</h3>
                    <div className="flex gap-[10px] flex-col">
                      {values.permissions.map((perm, idx) => (
                        <label key={perm.value} className="flex items-center gap-[8px]">
                          <input
                            type="checkbox"
                            checked={!!perm.selected}
                            onChange={(e) => {
                              const copy = [...values.permissions];
                              copy[idx] = { ...copy[idx], selected: e.target.checked };
                              setFieldValue("permissions", copy);
                            }}
                          />
                          <span className="text-[15px] text-gray-950 medium">{perm.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button type="submit" className="btn btn-pri w-full" disabled={isSubmitting}>
                    {isSubmitting ? t('advertiser.my_team.saving') : t('advertiser.my_team.submit')}
                  </button>

                  <div className="mt-3 text-center">
                    <button type="button" className="text-sm text-gray-600" onClick={closeCreateModal}>
                      {t('advertiser.my_team.cancel')}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <form method="dialog">
            <button className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full" onClick={closeCreateModal}>
              <img src="/images/close.svg" alt="close" />
              <span className="subtitle-2 bold">{t('advertiser.my_team.close')}</span>
            </button>
          </form>
        </dialog>
      )}

      {/* Confirm Delete */}
      {isConfirmOpen && (
        <dialog open className="modal p-[15px]" aria-modal>
          <div className="modal-box max-w-[420px] !w-full">
            <h3 className="headings-h3 text-zinc-800 mb-4">{t('advertiser.my_team.confirm_delete_title')}</h3>
            <p>
              {t('advertiser.my_team.confirm_delete_text')} <strong>{selectedToDelete?.name}</strong>?
            </p>

            <div className="flex gap-3 mt-6">
              <button className="btn btn-danger" onClick={handleDelete}>
                {t('advertiser.my_team.delete')}
              </button>
              <button className="btn btn-secondary" onClick={cancelDelete}>
                {t('advertiser.my_team.cancel')}
              </button>
            </div>
          </div>

          <form method="dialog">
            <button className="bg-white subtitle-2 flex items-center gap-[5px] px-[15px] py-[10px] rounded-full" onClick={cancelDelete}>
              <img src="/images/close.svg" alt="close" />
              <span className="subtitle-2 bold">{t('advertiser.my_team.close')}</span>
            </button>
          </form>
        </dialog>
      )}
    </>
  );
}
